import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { API_BASE } from "@/lib/utils";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";
  const [validating, setValidating] = useState(Boolean(token));
  const [tokenValid, setTokenValid] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) {
      setValidating(false);
      setTokenValid(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/auth/reset-password/validate?token=${encodeURIComponent(token)}`,
        );
        if (!cancelled) setTokenValid(res.ok);
      } catch {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `Reset failed (${res.status})`);
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      toast.error("Reset failed", { description: err?.message || "Try requesting a new link." });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8" style={{ background: "var(--bg-base)", color: "#FAFAFA" }}>
      <div className="w-full max-w-sm">
        <div className="mb-10"><Logo /></div>
        <div className="text-overline mb-3">Account recovery</div>
        <h1 className="font-heading text-3xl tracking-tight font-medium">Set new password</h1>

        {validating ? (
          <p className="mt-8 text-sm text-[var(--text-secondary)]">Checking reset link…</p>
        ) : done ? (
          <div className="mt-8 space-y-6">
            <div className="surface p-6 text-center">
              <CheckCircle className="mx-auto text-[var(--primary)] mb-3" size={32} />
              <p className="text-sm text-[var(--text-secondary)]">Your password has been updated. Sign in to continue.</p>
            </div>
            <button type="button" className="btn-primary w-full justify-center" onClick={() => navigate("/login")}>
              Sign in
            </button>
          </div>
        ) : !token || !tokenValid ? (
          <div className="mt-8 space-y-4">
            <p className="text-sm text-[var(--text-secondary)]">This link is invalid or has expired.</p>
            <Link to="/security" className="btn-secondary w-full justify-center inline-flex">Request a new link</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-password-form">
            <div>
              <label className="text-overline">New password</label>
              <input
                type="password"
                className="input-dark mt-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-password-input"
              />
            </div>
            <div>
              <label className="text-overline">Confirm password</label>
              <input
                type="password"
                className="input-dark mt-2"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                data-testid="reset-confirm-password-input"
              />
            </div>
            <button type="submit" className="btn-primary w-full justify-center mt-2" disabled={busy} data-testid="reset-submit">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}

        <Link to="/login" className="mt-8 flex items-center justify-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--primary)]">
          <ArrowLeft size={14} /> Back to login
        </Link>
      </div>
    </div>
  );
}
